/**
 * displayWorkflowVersion — the AM-facing "Version" string for a run.
 *
 * The substrate registry's ``major_version`` is the INPUT-CONTRACT version of
 * a ``workflow_id`` (a dispatch key — ``start_workflow`` resolves
 * ``(workflow_id, major_version)``, defaulting to 1), while rebuild
 * generations carry their generation in the id itself:
 * ``carrier_mortgagee_update_v2`` registers as ``major_version=1`` — the
 * first contract of the v2 rebuild. Rendering the raw registry number reads
 * as a contradiction next to a title like "(v2 — Agent Runtime)" (DEV
 * instance f091536d, 2026-07-14).
 *
 * Resolution order: a version suffix embedded in the workflow_id, then the
 * registry number as the fallback for ids that don't carry one:
 *
 *   carrier_mortgagee_update_v2  -> "v2"
 *   mortgagee_update_v1_2        -> "v1.2"
 *   policy_check_v3a             -> "v3a"
 *   endorsement_create (major 1) -> "v1"
 *
 * There is no override table any more. ``carrier_mortgagee_update_v2`` used to
 * have one, forcing its chip to read the generation's OLD name because that is
 * what the AMs called it at the time. That name is retired: the workflows
 * registry renamed its own AM-facing title to "Carrier Mortgagee Update v2"
 * (story 26561) precisely because the label and the id disagreeing by one
 * generation cost real confusion — the two add-in panes read different fields
 * of the SAME row, each showed a different generation, and an AM reasonably
 * read that as two workflows competing for one email. Keeping the override
 * here would have re-opened that gap from the other side. (Story 28633 later
 * renamed it again, to "Mortgagee Change" — a name with no generation in it.)
 *
 * The id itself is still deliberately NOT renamed — it is the registry
 * dispatch key, the Durable Task orchestration name, a persisted
 * ``workflow_id`` column value, and a hardcoded constant across this repo, the
 * workflows repo and the mcp repo. Renaming it would strand in-flight
 * instances and split run history. It simply no longer needs to disagree with
 * what the label says.
 *
 * Display-only — never feed this back into launch/dispatch calls, where the
 * registry ``major_version`` remains the source of truth.
 */

export function displayWorkflowVersion(
  workflowId: string | null | undefined,
  majorVersion: number | null | undefined,
): string | null {
  const m = workflowId ? /_v(\d+[a-z]?(?:_\d+)*)$/.exec(workflowId) : null;
  if (m) return `v${m[1].replace(/_/g, ".")}`;
  return majorVersion != null ? `v${majorVersion}` : null;
}

export default displayWorkflowVersion;
